import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import GoogleSignIn from '@/components/GoogleSignIn';
import Logo from '@/components/Logo';
import api, { formatError } from '@/lib/api';

function AuthLayout({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background dot-grid px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" aria-label="Rayogent home" className="inline-flex"><Logo size="lg" /></Link>
        </div>
        <div className="neo-card p-6 md:p-8">
          {children}
        </div>
        <p className="mt-5 text-xs text-muted-foreground flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Your money stays in your wallet until your ad runs.
        </p>
      </motion.div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-xl font-bold mb-1 font-heading">Welcome back</h2>
      <p className="text-sm text-muted-foreground mb-6">Log in to manage your ads</p>
      {error && <div className="mb-4 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="login-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@business.com" required className="mt-1.5" data-testid="login-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm font-medium">Password</Label>
          <div className="relative mt-1.5">
            <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required data-testid="login-password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-bg h-11" data-testid="login-form-submit-button">
          {loading ? 'Logging in...' : 'Log In'} {!loading && <ArrowRight className="w-4 h-4 ml-1" />}
        </Button>
      </form>
      <GoogleSignIn text="signin_with" onError={setError} />
      <p className="text-sm text-center mt-5 text-muted-foreground">
        Don't have an account? <Link to="/register" className="text-brand-strong hover:underline font-medium" data-testid="register-link">Sign up</Link>
      </p>
    </AuthLayout>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/business-profile');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-xl font-bold mb-1 font-heading">Create your account</h2>
      <p className="text-sm text-muted-foreground mb-6">Start running ads in minutes</p>
      {error && <div className="mb-4 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="register-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-sm font-medium">Full Name</Label>
          <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required className="mt-1.5" data-testid="register-name-input" />
        </div>
        <div>
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@business.com" required className="mt-1.5" data-testid="register-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm font-medium">Password</Label>
          <div className="relative mt-1.5">
            <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required minLength={6} data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-bg h-11" data-testid="register-form-submit-button">
          {loading ? 'Creating account...' : 'Get started'} {!loading && <ArrowRight className="w-4 h-4" />}
        </Button>
      </form>
      <GoogleSignIn text="signup_with" onError={setError} />
      <p className="text-sm text-center mt-5 text-muted-foreground">
        Already have an account? <Link to="/login" className="text-brand-strong hover:underline font-medium" data-testid="login-link">Log in</Link>
      </p>
    </AuthLayout>
  );
}

export function ChangePasswordPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('Your new password must be at least 8 characters.'); return; }
    setLoading(true);
    try {
      await api.post('/api/auth/change-password', { current_password: current, new_password: next });
      await refreshUser();
      navigate(['admin', 'super_admin'].includes(user?.role) ? '/admin' : '/dashboard');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-xl font-bold mb-1 font-heading">Set a new password</h2>
      <p className="text-sm text-muted-foreground mb-6">
        {user?.must_change_password ? 'For your security, choose your own password before continuing.' : 'Choose a new password.'}
      </p>
      {error && <div className="mb-4 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="change-password-error">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="current" className="text-sm font-medium">Current password</Label>
          <Input id="current" type="password" value={current} onChange={e => setCurrent(e.target.value)} required className="mt-1.5" data-testid="current-password-input" />
        </div>
        <div>
          <Label htmlFor="next" className="text-sm font-medium">New password</Label>
          <Input id="next" type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8} className="mt-1.5" placeholder="At least 8 characters" data-testid="new-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full gradient-bg h-11" data-testid="change-password-submit">
          {loading ? 'Saving...' : 'Save password'}
        </Button>
        <button type="button" onClick={async () => { await logout(); navigate('/login'); }} className="w-full text-xs text-muted-foreground hover:text-foreground">Log out</button>
      </form>
    </AuthLayout>
  );
}
